/**
 * UlkeSecici.jsx — ülkeyi ADIYLA seçtiren, aranabilir seçici (backlog #554).
 *
 * Eskiden "Ülke Kodu" diye country tablosunun sayısal id'sini yazdıran kutuların yerini alır; kodu seçilen satırdan biz
 * göndeririz. Liste GET /portal/ulkeler'den gelir (isteğin dilinde, gösterilen ada göre sıralı, 206 ülke) ve dil başına bir kez
 * okunur. Başa açık bir "Bilinmiyor" seçeneği konur (country_id 0, bkz. firmaUyruk.js): ülkesi bilinmeyen firma boş kutu değil
 * "Bilinmiyor" gösterir. Liste okunamazsa seçici yalnızca Bilinmiyor + mevcut değerle çalışır ve bunu söyler.
 *
 * @param {number|string|null} value   ülke id'si (0 = Bilinmiyor)
 * @param {Function}           onChange (ulkeId:number) => void
 * @param {boolean}            turkiyeHaric  Yabancı firma: Türkiye seçilemez (Türkiye = Yerli)
 */
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { getUlkeler } from "../../services/portalService";
import { secilenUlke, ulkeSecenekleri } from "./firmaUyruk";

// dil → Promise<[{countryId, countryName}]>. Hata önbelleğe alınmaz: sonraki açılış yeniden dener.
const ONBELLEK = new Map();

function ulkeleriOku(dil) {
  if (!ONBELLEK.has(dil)) {
    const istek = getUlkeler().then((liste) => (Array.isArray(liste) ? liste : []));
    istek.catch(() => ONBELLEK.delete(dil));
    ONBELLEK.set(dil, istek);
  }
  return ONBELLEK.get(dil);
}

// Tema değişkenleri: koyu temada seçici beyaz kalmasın.
const STILLER = {
  control: (b, s) => ({
    ...b, minHeight: 31, fontSize: ".8125rem",
    backgroundColor: "var(--vz-input-bg-custom, var(--vz-secondary-bg, #fff))",
    borderColor: s.isFocused ? "var(--vz-primary, #405189)" : "var(--vz-input-border-custom, var(--vz-border-color, #ced4da))",
    boxShadow: "none",
  }),
  singleValue: (b) => ({ ...b, color: "var(--vz-body-color, inherit)" }),
  input: (b) => ({ ...b, color: "var(--vz-body-color, inherit)" }),
  menu: (b) => ({ ...b, backgroundColor: "var(--vz-secondary-bg, #fff)", fontSize: ".8125rem" }),
  option: (b, s) => ({
    ...b, color: "var(--vz-body-color, inherit)",
    backgroundColor: s.isSelected ? "var(--vz-primary, #405189)" : s.isFocused ? "var(--vz-tertiary-bg, #f3f6f9)" : "transparent",
  }),
  menuPortal: (b) => ({ ...b, zIndex: 2000 }),   // modalın (1055) üstünde
};

export default function UlkeSecici({ value, onChange, turkiyeHaric = false, disabled = false, invalid = false, inputId }) {
  const { t, i18n } = useTranslation("components");
  const dil = i18n?.language || "tr";
  const [ulkeler, setUlkeler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(false);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(false);
      try {
        const liste = await ulkeleriOku(dil);
        if (!ignore) setUlkeler(liste);
      } catch {
        if (!ignore) setHata(true);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [dil]);

  const bilinmiyor = t("ulkeSecici.bilinmiyor");
  // Seçili değer her zaman tüm listeden aranır (Yerli: Türkiye gösterilir), açılan liste ise turkiyeHaric'e uyar.
  const tum = useMemo(() => ulkeSecenekleri(ulkeler, bilinmiyor), [ulkeler, bilinmiyor]);
  const secenekler = useMemo(
    () => (turkiyeHaric ? ulkeSecenekleri(ulkeler, bilinmiyor, { turkiyeHaric: true }) : tum),
    [ulkeler, bilinmiyor, turkiyeHaric, tum],
  );

  return (
    <>
      <Select
        inputId={inputId}
        classNamePrefix="ulke-secici"
        className={invalid ? "is-invalid" : undefined}
        options={secenekler}
        value={secilenUlke(tum, value)}
        onChange={(secim) => secim && onChange?.(secim.value)}
        isDisabled={disabled}
        isLoading={yukleniyor}
        placeholder={t("ulkeSecici.ulkeSecin")}
        loadingMessage={() => t("ulkeSecici.yukleniyor")}
        noOptionsMessage={() => t("ulkeSecici.ulkeBulunamadi")}
        styles={STILLER}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPlacement="auto"
      />
      {hata && <div className="text-danger small mt-1">{t("ulkeSecici.listeOkunamadi")}</div>}
    </>
  );
}
